<app-card-shell padding="lg">
  <div class="flex flex-col gap-5">
    <div class="flex items-center justify-between gap-3">
      <h2
        class="text-sm font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider m-0"
      >
        {{ 'COMPONENTS.SALES_ORDERS.SHIPMENTS.TITLE' | translate }}
      </h2>

      @if (canCreateShipment && !disabled) {
        <button
          type="button"
          class="bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors"
          (click)="requestCreateShipment()"
        >
          + {{ 'COMPONENTS.SALES_ORDERS.SHIPMENTS.NEW_BUTTON' | translate }}
        </button>
      }
    </div>

    @if (isLoading) {
      <div
        class="text-center py-12 text-sm text-neutral-500 dark:text-neutral-400"
        role="status"
        aria-live="polite"
      >
        {{ 'MESSAGES.LOADING' | translate }}
      </div>
    } @else if (hasLoadError) {
      <div
        class="flex flex-col items-center gap-3 px-4 py-3 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
        role="alert"
      >
        <p class="m-0 text-sm">
          {{ 'COMPONENTS.SALES_ORDERS.ERRORS.SHIPMENT_FAILED' | translate }}
        </p>
        <button
          type="button"
          class="px-3 py-1.5 text-sm font-medium rounded-md border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
          (click)="retry()"
        >
          {{ 'BUTTONS.RETRY' | translate }}
        </button>
      </div>
    } @else if (shipmentRows$ | async; as shipmentRows) {
      @if (shipmentRows.length === 0) {
        <div class="text-center py-12 text-sm text-neutral-400 dark:text-neutral-500">
          {{ 'COMPONENTS.SALES_ORDERS.SHIPMENTS.EMPTY_STATE' | translate }}
        </div>
      } @else {
        <div class="flex flex-col gap-2">
          @for (row of shipmentRows; track row.id) {
            <div
              class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 p-4 rounded-xl border border-neutral-100 dark:border-neutral-800 hover:border-neutral-200 dark:hover:border-neutral-700 focus-visible:border-neutral-200 dark:focus-visible:border-neutral-700 focus-visible:outline-none transition-colors"
            >
              <div class="flex items-center gap-3 min-w-0 sm:flex-1">
                <div
                  class="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  [class]="row.iconBgClass"
                  aria-hidden="true"
                >
                  <svg
                    class="w-5 h-5"
                    [class]="row.iconTextClass"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 7a2 2 0 012-2h9a2 2 0 012 2v8H3V7zm13 3h3l2 3v2a2 2 0 01-2 2h-1m-2 0H5"
                    />
                    <circle cx="7" cy="17" r="2" stroke-width="2" />
                    <circle cx="17" cy="17" r="2" stroke-width="2" />
                  </svg>
                </div>

                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-neutral-900 dark:text-neutral-50 m-0 break-words sm:truncate">
                    {{ row.number }}
                  </p>
                  <p class="text-xs text-neutral-400 dark:text-neutral-500 m-0 mt-0.5">
                    {{ row.shipmentDate | localizedDate }}
                  </p>
                </div>
              </div>

              <div class="flex flex-wrap items-center gap-2 sm:flex-col sm:items-end sm:gap-1.5 sm:shrink-0">
                <span
                  class="text-xs font-semibold px-2 py-0.5 rounded-full sm:order-2"
                  [class]="row.statusBadgeClass"
                >
                  {{ row.statusLabelKey | translate }}
                </span>
                <p class="text-xs text-neutral-500 dark:text-neutral-400 m-0 tabular-nums sm:order-1 w-full sm:w-auto sm:text-right">
                  {{ 'COMPONENTS.SALES_ORDERS.SHIPMENTS.LINE_COUNT' | translate }}: {{ row.lineCount }}
                  · {{ 'COMPONENTS.SALES_ORDERS.SHIPMENTS.TOTAL_QTY' | translate }}: {{ row.totalBaseQuantity | number: '1.0-2' }}
                </p>
                @if (row.canMarkShipped && !disabled) {
                  <button
                    type="button"
                    class="px-3 py-1 text-xs font-medium rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30 disabled:opacity-60 disabled:cursor-progress transition-colors sm:order-3"
                    [attr.data-row-id]="row.id"
                    [disabled]="row.isMutating"
                    (click)="markShipped(row)"
                  >
                    @if (row.isMutating) {
                      {{ 'COMPONENTS.SALES_ORDERS.SHIPMENTS.MARK_SHIPPED_LOADING' | translate }}
                    } @else {
                      {{ 'COMPONENTS.SALES_ORDERS.SHIPMENTS.MARK_SHIPPED' | translate }}
                    }
                  </button>
                }
                @if (row.errorKey; as errorKey) {
                  <p class="text-xs text-red-600 dark:text-red-400 m-0 w-full sm:w-auto sm:text-right sm:order-4" role="alert">
                    {{ errorKey | translate }}
                  </p>
                }
              </div>
            </div>
          }
        </div>
      }
    }
  </div>
</app-card-shell>

